<template>
  <Window
    class="cc-drilldown-window"
    :title="title"
    :modal="true"
    :width="windowWidth"
    :height="windowHeight"
    @close="$emit('close')"
  >
    <div class="cc-drilldown-content">
      <div v-if="loading" class="cc-drilldown-loading">
        <Loader />
      </div>
      <div v-else-if="error" class="cc-drilldown-error">
        {{ error }}
      </div>
      <Grid
        v-else
        :class="{ 'cc-drilldown-clickable': rowsAreClickable }"
        :data-items="rows"
        :columns="columns"
        :resizable="true"
        :sortable="true"
        @rowclick="onRowClick"
      />
    </div>
  </Window>

  <!-- Business details for the clicked row's consultant. A sibling of the
       drill-down window (not nested in it) so it isn't clipped to its bounds
       and stacks on top of it. -->
  <Window
    v-if="businessDetailsBeeNumber !== null"
    class="business-details-window"
    :title="t('ccConsultantDetail.BusinessDetails')"
    :modal="true"
    :maximize-button="true"
    :width="businessDetailsWidth"
    :height="businessDetailsHeight"
    @close="businessDetailsBeeNumber = null"
  >
    <BusinessDetails
      :beeNumber="businessDetailsBeeNumber"
      @close="businessDetailsBeeNumber = null"
    />
  </Window>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { Grid } from '@progress/kendo-vue-grid'
import type { GridRowClickEvent } from '@progress/kendo-vue-grid'
import { Window } from '@progress/kendo-vue-dialogs'
import { Loader } from '@progress/kendo-vue-indicators'
import { useI18n } from 'vue-i18n'
import { PwsDynamicReportGet, PwsDynamicReportExecute } from '@/api/ConsultantCorner'
import { resolveColumnWidth } from '@/utils/kendoHelper.ts'
import BusinessDetails from '@/components/BusinessDetails.vue'

const { t } = useI18n()

interface Props {
  // Child report id, conventionally `${parentReportId}_${columnName}`.
  reportId: string
  // Localized dialog heading (the clicked column's localized name).
  title: string
  // Consultant to scope the report to; authorized server-side against the
  // caller's downline. Null runs it for the caller.
  beeNumberOverride?: number | null
  // Server-side filters carried over from the parent report (e.g. the chosen
  // batch/period) so the drill-down reconciles with the count that was clicked.
  filters?: any[]
}

const props = withDefaults(defineProps<Props>(), {
  beeNumberOverride: null,
  filters: () => []
})

defineEmits<{ close: [] }>()

const loading = ref(true)
const error = ref('')
const rows = ref<any[]>([])
const columns = ref<any[]>([])

const windowWidth = ref(900)
const windowHeight = ref(600)

// Clicking a row opens that consultant's business details in a second popup.
// Sized like the consultant detail card's business details window.
const businessDetailsBeeNumber = ref<number | null>(null)
const businessDetailsWidth = ref(1200)
const businessDetailsHeight = ref(800)

onMounted(() => {
  windowWidth.value = Math.min(900, window.innerWidth - 40)
  windowHeight.value = Math.min(600, window.innerHeight - 40)

  const isMobile = window.innerWidth < 768
  businessDetailsWidth.value = isMobile ? window.innerWidth - 20 : Math.floor(window.innerWidth * 0.9)
  businessDetailsHeight.value = isMobile ? window.innerHeight - 20 : Math.floor(window.innerHeight * 0.9)
})

// A row's consultant: the BeeNumber column if the report has one, otherwise the
// "Consultant Number:" line of the stacked ConsultantInfo column.
function getBeeNumberFromRow(row: any): number | null {
  if (row?.BeeNumber !== undefined && row?.BeeNumber !== null && row?.BeeNumber !== '') {
    const beeNumber = Number(row.BeeNumber)
    if (!isNaN(beeNumber)) return beeNumber
  }
  if (typeof row?.ConsultantInfo === 'string') {
    const match = row.ConsultantInfo.match(/Consultant Number:\s*(\d+)/)
    if (match) return parseInt(match[1], 10)
  }
  return null
}

// Only show the pointer cursor when the report's rows identify a consultant
const rowsAreClickable = computed(() => rows.value.some(row => getBeeNumberFromRow(row) !== null))

function onRowClick(event: GridRowClickEvent) {
  const beeNumber = getBeeNumberFromRow(event.dataItem)
  if (beeNumber !== null) {
    businessDetailsBeeNumber.value = beeNumber
  }
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const definition = await PwsDynamicReportGet(props.reportId)

    const visibleColumns = definition.item.columns
      .filter(col => col.columnName !== 'ParentBeeNumber' && !col.isFilterOnly)
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))

    columns.value = visibleColumns.map(col => ({
      field: col.columnName,
      title: col.localizedColumnName,
      width: `${resolveColumnWidth(col.minWidth, col.localizedColumnName)}px`
    }))

    // Start from the child report's own default filters, then let the parent's
    // server filters override per column (same merge the main report uses).
    let allFilters = [...(definition.item.filters || [])]
    ;(props.filters || []).forEach(parentFilter => {
      allFilters = allFilters.filter(f => f.columnName !== parentFilter.columnName)
      allFilters.push(parentFilter)
    })

    const result = await PwsDynamicReportExecute({
      reportId: props.reportId,
      ColumnNames: visibleColumns.map(col => col.columnName),
      Filters: allFilters,
      BeeNumberOverride: props.beeNumberOverride ?? undefined
    })

    rows.value = result.Items || []
  } catch (err: any) {
    error.value = err?.message || t('ccReport.DrilldownLoadError')
  } finally {
    loading.value = false
  }
}

// Reload when the target column/consultant changes while the dialog is open.
watch(
  () => [props.reportId, props.beeNumberOverride],
  () => load(),
  { immediate: true }
)
</script>

<style lang="scss" scoped>
.cc-drilldown-content {
  min-height: 200px;
}

.cc-drilldown-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.cc-drilldown-clickable :deep(tbody tr) {
  cursor: pointer;
}

.cc-drilldown-error {
  color: red;
  padding: $card-padding;
  text-align: center;
}
</style>
